/* eslint-disable react/no-unknown-property */
/* eslint-disable jsx-a11y/media-has-caption */

'use client'

import { FLAIR_IMG, PLAY_ICON } from '@/components/RevampedLanding/contants'
import { FacultyProps } from '@/widgets/landing-page-widgets/FacultySection/schema'
import { Dialog } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Typography } from '@pw-tech/omni-ui'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'



const FacultyModal = ({
  open,
  onClose,
  data,
}: {
  open: boolean
  onClose: () => void
  data: FacultyProps
}) => {
  const [play, setPlay] = useState(false)
  const [muted, setMuted] = useState(true)
  const videoRef = useRef<HTMLVideoElement>(null)

  const handleVideoClick = () => {
    const video = videoRef.current
    if (!video) return

    // Unmute the video on first click (user interaction)
    if (muted) {
      setMuted(false)
      video.muted = false
    }

    if (video.paused) {
      const playPromise = video.play()
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          if (error.name === 'NotAllowedError') {
            console.log('Video autoplay prevented - user interaction required')
          } else {
            console.error('Error playing video:', error)
          }
        })
      }
    } else {
      video.pause()
    }
  }

  const handleImageContainerClick = () => {
    setPlay(true)
  }



  // Auto-play video when it becomes available (with error handling)
  useEffect(() => {
    if (play && videoRef.current) {
      const video = videoRef.current
      // Enable audio immediately when video starts (user has already interacted by clicking play)
      setMuted(false)
      video.muted = false
      // Small delay to ensure video element is fully loaded
      setTimeout(() => {
        const playPromise = video.play()
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            if (error.name === 'NotAllowedError') {
              console.log('Video autoplay prevented - user interaction required')
            } else {
              console.error('Error playing video:', error)
            }
          })
        }
      }, 100)
    }
  }, [play])

  return (
    <Dialog className="relative z-[999999]" open={open} onClose={onClose}>
      <div className="fixed inset-0" aria-hidden='true' style={{ background: 'rgba(0,0,0,0.6)' }} />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto md:w-[520px] w-[min(98vw,340px)] p-4 md:p-6 transform divide-y overflow-hidden rounded-2xl md:rounded-3xl bg-white transition-all">
          <div className="flex flex-col gap-4">
            <div className="flex flex-row gap-3 justify-between">
              <div className="flex flex-col gap-2">
                <Typography variant='heading2' weight='bold' color='#1b2124'>
                  {data.name}
                </Typography>
                <Typography variant='subHeading' weight='semi-bold' color='#1b2124'>
                  {data.details.designation}
                </Typography>
              </div>
              <XMarkIcon onClick={onClose} className="relative top-1 w-5 h-5 md:w-6 md:h-6 cursor-pointer" />
            </div>

            <div className="min-w-[200px] w-full md:h-[245px] h-[180px] relative rounded-2xl border-2 border-gray-200/50 mx-auto cursor-pointer overflow-hidden" onClick={handleImageContainerClick}>
              {data?.image && (
                <Image
                  src={data.image}
                  alt={data.name}
                  objectFit='contain'
                  layout='fill'
                  className='rounded-2xl'
                />
              )}

              {data?.video && !play && (
                <div className="absolute inset-0 w-full h-full bg-black300">
                  <div className="w-full h-full flex justify-center items-center">
                    <Image src={PLAY_ICON} objectFit='contain' alt='play-icon' width={56} height={56} />
                  </div>
                </div>
              )}

              {play && (
                <div className="absolute inset-0 w-full h-full overflow-hidden" onClick={handleVideoClick}>
                  <video 
                    muted={muted} 
                    loop 
                    playsInline 
                    autoPlay
                    className="w-full h-full rounded-lg" 
                    ref={videoRef}
                  >
                    <source src={data?.video} type='video/mp4' />
                    Your browser does not support the video tag.
                  </video>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {data?.details?.multi_description?.map(({ description }, index) => (
                <Typography variant='regular' key={index} className="flex flex-row gap-[6px]" color='#1b2124'>
                  <Image src={FLAIR_IMG} width={16} height={16} alt='flair' />
                  {description}
                </Typography>
              ))}
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default FacultyModal
